/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import Cookies from 'js-cookie';
import { Box, Stack, MenuItem, TextField, CircularProgress } from '@mui/material';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

import '../styles/pages/programs.scss';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';
import ScheduleAtAGlance from '../components/editorial/ScheduleAtAGlance';

import SocialLoginModal from '../components/modal/SocialLoginModal';
import { googleAuth, appleAuth } from '../service/socialAuth';
import { updateUserPhone } from '../api/users/saveUsers';
import { eventsData } from '../api/events/eventsData';
import useFirebaseUser from '../hooks/useFirebaseUser';

const quantityFieldSx = {
  width: 90,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(36,34,88,0.25)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--teal-500) !important',
  },
  '& .MuiSelect-select': {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    letterSpacing: '0.16em',
    color: 'var(--navy-500)',
  },
};

// The two weekday Shacharit minyanim called out in the kehilla bulletin.
const SCHEDULE = [
  ["6:45 AM", "Shacharit  ·  Early Minyan"],
  ["8:15 AM", "Shacharit  ·  Second Minyan"],
];

export default function Prayers() {
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const { user } = useFirebaseUser();
  const uid = user?.uid;
  const [loading, setLoading] = React.useState(true);
  const [eventTickets, setEventTickets] = React.useState([]);
  const [quantities, setQuantities] = React.useState({});
  const [isTicketSelected, setIsTicketSelected] = React.useState(false);
  const [ticketsForPayment, setTicketsForPayment] = React.useState([]);

  const handleQuantityChange = (e, idx) => {
    setQuantities((p) => ({ ...p, [idx]: e.target.value }));
  };

  const handleAddToCart = () => {
    const selected = [];
    let total = 0;
    let finalArray = [];
    eventTickets.forEach((ticket, index) => {
      const q = quantities[index] || 0;
      if (q > 0) {
        total += ticket.ticketPrice * q;
        selected.push({
          ticketName: ticket.ticketTitle,
          EventName: ticket.eventTitle,
          amount: ticket.ticketPrice,
          description: ticket.ticketDesc,
          quantity: q,
        });
        finalArray = [...selected, { totalAmount: total }];
      }
    });
    setAmount(total);
    if (selected.length) {
      if (!isTicketSelected) toast.success('You are ready for tickets payment!');
      setIsTicketSelected(true);
      setTicketsForPayment(finalArray);
    } else if (uid) {
      toast.error('Please Select a Ticket Quantity!');
    }
  };

  React.useEffect(() => {
    (async () => {
      const response = await eventsData();
      if (response?.data?.data) {
        const filtered = response.data.data.filter((e) => e.eventType === 'Purchasable');
        const allTickets = filtered.reduce((acc, ev) => {
          const ticketsWithTitle = (ev.tickets || []).map((t) => ({ ...t, eventTitle: ev.eventTitle }));
          return acc.concat(ticketsWithTitle);
        }, []);
        setEventTickets(allTickets);
      }
      setLoading(false);
    })();
  }, []);

  const handleGoogle = async () => {
    try {
      const res = await googleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleApple = async () => {
    try {
      const res = await appleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleAccountCreated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) Cookies.set('customerStripeID', res.customerStripeID);
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  return (
    <EditorialPageShell
      title="Prayers"
      description="Daily and Shabbat minyanim at KBT — Kehilat Bnei Torah, Los Angeles."
    >
      <PageHero
        kicker="Prayer  ·  KBT"
        meta="Daily minyanim"
        title="A kehilla,"
        titleAccent="in prayer."
        subtitle="Three tefillot a day, every day. Two morning minyanim on weekdays — early and late — and a full Shabbat schedule. The doors are open."
        image="/assets/images/kbt/prayers-hero.jpg"
        height="80vh"
      />

      <EditorialSection
        kicker="Two Minyanim"
        title="Mornings,"
        titleAccent="early and late."
        intro="Weekdays bring two Shacharit options — Neitz-style at first light and a Second Minyan later in the morning. Both held in the sanctuary; please respect the kehilla's no-talking rule."
        align="center"
        watermark="פ"
      >
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Stack spacing={2}>
            {SCHEDULE.map(([t, l]) => (
              <Box
                key={l}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr',
                  alignItems: 'center',
                  gap: 4,
                  borderTop: '1px solid rgba(36,34,88,0.15)',
                  py: 3,
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 13,
                    letterSpacing: '0.18em',
                    color: 'var(--teal-500)',
                  }}
                >
                  {t}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: { xs: 20, md: 26 },
                    color: 'var(--navy-500)',
                    lineHeight: 1.3,
                  }}
                >
                  {l}
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                borderTop: '1px solid rgba(36,34,88,0.15)',
                pt: 4,
                fontFamily: 'var(--font-body)',
                fontStyle: 'italic',
                fontSize: 15,
                lineHeight: 1.6,
                color: 'var(--text)',
                opacity: 0.7,
                textAlign: 'center',
              }}
            >
              These are sensitive, life-changing days. We ask everyone to respect the no-talking rule during the services.
            </Box>
          </Stack>
        </Box>
      </EditorialSection>

      {/* Ticket-reservation section — only renders when there are
          actually purchasable tickets in the events feed. When the high-
          holiday window reopens (or any other paid event goes live), the
          section will appear automatically without code changes. */}
      {(loading || eventTickets.length > 0) && (
      <EditorialSection
        background="var(--paper-warm)"
        kicker="Reserve"
        title="Seat"
        titleAccent="reservations."
        intro="Select how many seats you'd like in each tier and continue to checkout."
      >
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: 'var(--navy-500)' }} />
            </Box>
          ) : (
            <>
              <Stack spacing={0}>
                {eventTickets.map((ticket, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.7, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Box
                      sx={{
                        py: 3,
                        borderTop: '1px solid rgba(36,34,88,0.15)',
                        ...(index === eventTickets.length - 1 && {
                          borderBottom: '1px solid rgba(36,34,88,0.15)',
                        }),
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                        gap: 2,
                        alignItems: 'center',
                      }}
                    >
                      <Stack spacing={1}>
                        <Box
                          sx={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700,
                            fontSize: { xs: 20, md: 24 },
                            color: 'var(--navy-500)',
                            lineHeight: 1.2,
                          }}
                        >
                          {ticket.ticketTitle}
                        </Box>
                        <Box
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            letterSpacing: '0.18em',
                            color: 'var(--teal-500)',
                          }}
                        >
                          ${ticket.ticketPrice}
                        </Box>
                        {ticket.ticketDesc && (
                          <Box
                            sx={{
                              fontFamily: 'var(--font-body)',
                              fontSize: 15,
                              lineHeight: 1.6,
                              color: 'var(--text)',
                              opacity: 0.75,
                            }}
                          >
                            {ticket.ticketDesc}
                          </Box>
                        )}
                      </Stack>
                      <TextField
                        select
                        defaultValue="0"
                        onChange={(e) => handleQuantityChange(e, index)}
                        sx={quantityFieldSx}
                      >
                        {[...Array(10)].map((_, i) => (
                          <MenuItem key={i} value={i}>
                            {i}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </motion.div>
                ))}
              </Stack>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 4 }}>
                <EditorialButton
                  variant="secondary"
                  onClick={() => {
                    handleAddToCart();
                    localStorage.setItem('amount', amount);
                    localStorage.setItem('paymentType', 'ticket');
                    localStorage.setItem('paymentTypeName', 'Event Ticket');
                    localStorage.setItem('tickets', JSON.stringify(ticketsForPayment));
                    if (!uid) setOpen(true);
                  }}
                  href={isTicketSelected && amount !== '' && uid ? '/payment-checkout' : null}
                >
                  Continue
                </EditorialButton>
              </Box>
            </>
          )}
        </Box>
      </EditorialSection>
      )}

      {/* Full weekly schedule (same component used on the home page). */}
      <ScheduleAtAGlance />

      <SocialLoginModal
        open={open}
        handleClose={() => setOpen(false)}
        googleLogin={handleGoogle}
        appleLogin={handleApple}
        onAccountCreated={handleAccountCreated}
        requirePhoneVerification={true}
      />
    </EditorialPageShell>
  );
}
